<template>
  <div
    class="rte-content rte"
    v-html="formattedContent"
  />
</template>

<script>

/**
 * Vue: RTE content (rte-content)
 * -----------------------------------------------------------------------------
 * Component to standardise output of Shopify RTE content.
 *
 * @param {String} content - RTE content.
 * @param {String} [fontSize] - Paragraph font size.
 * @param {Boolean|String} [fontSizeDesktop] - Desktop paragraph font size.
 * @param {Number} [imageWidth] - RTE content image width.
 * @param {Boolean} [isOverlay] - If rendered in an overlay.
 *
 */
export default {
  name: 'RteContent',

  props: {
    content: {
      type: String,
      default: '',
      required: true,
    },
    fontSize: {
      type: String,
      default: 'm',
      validator: (value) => {
        return ['2xs', 'xs', 's', 'm', 'l'].includes(value)
      },
    },
    fontSizeDesktop: {
      type: [Boolean, String],
      default: false,
      validator: (value) => {
        return [false, '2xs', 'xs', 's', 'm', 'l'].includes(value)
      },
    },
    imageWidth: {
      type: Number,
      default: 920,
    },
    isOverlay: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Compute desktop font size - default to `this.fontSize` if no desktop
     * version was specified.
     * @returns {String}
     */
    desktopFontSize() {
      return this.fontSizeDesktop ? this.fontSizeDesktop : this.fontSize
    },

    /**
     * Format content with standard classes and update image size.
     * @returns {String}
     */
    formattedContent() {
      let content = this.content
        .replace(/(?:480x480|600x600|1024x1024|2048x2048)/g, `${this.imageWidth}x`)
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('<p><img', '<img')
        .replaceAll('alt></p>', 'alt>')
        .replaceAll('"></p>', '">')
        .replaceAll('<meta charset="utf-8">', '')
        .replaceAll('<p', `<p class="text-body-${this.fontSize}-regular text-body-${this.desktopFontSize}-regular-desktop"`)
        .replaceAll('<strong', `<strong class="text-body-${this.fontSize}-bold text-body-${this.desktopFontSize}-bold-desktop"`)
        .replaceAll('<li', `<li class="text-body-${this.fontSize}-regular text-body-${this.desktopFontSize}-regular-desktop"`)
        .replaceAll('<a', `<a class="text-body-${this.fontSize}-underline text-body-${this.desktopFontSize}-underline-desktop"`)
        .replaceAll('<th>', '<th class="text-body-s-bold text-body-m-bold-desktop">')
        .replaceAll('<th ', '<th class="text-body-s-bold text-body-m-bold-desktop" ')
        .replaceAll('<td', '<td class="text-body-s-regular text-body-m-regular-desktop"')
        .replaceAll('<b>', '')
        .replaceAll('</b>', '')
        .replaceAll('style=', 'disabled-style=')

      if (this.isOverlay) {
        content = content
          .replaceAll('<h1', '<h1 class="text-heading-xl"')
          .replaceAll('<h2', '<h2 class="text-heading-l"')
          .replaceAll('<h3', '<h3 class="text-heading-m"')
          .replaceAll('<h4', '<h4 class="text-heading-s"')
          .replaceAll('<h5', '<h5 class="text-heading-s"')
          .replaceAll('<h6', '<h6 class="text-heading-s"')
      } else {
        content = content
          .replaceAll('<h1', '<h1 class="text-heading-5xl text-heading-6xl-desktop"')
          .replaceAll('<h2', '<h2 class="text-heading-4xl text-heading-5xl-desktop"')
          .replaceAll('<h3', '<h3 class="text-heading-3xl text-heading-4xl-desktop"')
          .replaceAll('<h4', '<h4 class="text-heading-2xl text-heading-3xl-desktop"')
          .replaceAll('<h5', '<h5 class="text-heading-xl text-heading-2xl-desktop"')
          .replaceAll('<h6', '<h6 class="text-heading-l text-heading-xl-desktop"')
      }

      return content
    },
  },
}
</script>
